'use client';
import { useRoom } from './useRoom';
import { useCallback, useEffect, useState } from 'react';
import type { RemotePeer } from '@huddle01/web-core';
import type {
  NewPeerJoined,
  RoomEvents,
  TPermissions,
} from '@huddle01/web-core/types';

export const useRemotePeer = <T = unknown>(props: {
  peerId: string;
  onMetadataUpdate?: (data: { metadata: unknown }) => void;
}) => {
  const { room } = useRoom();

  const [remotePeer, setRemotePeer] = useState<RemotePeer | null>(
    room.remotePeerExists(props.peerId) || null,
  );

  const [metadata, setMetadata] = useState<T | null>(
    remotePeer?.getMetadata<T>() || null,
  );

  const [role, setRole] = useState<RemotePeer['role'] | null>(
    remotePeer?.role || null,
  );

  const handleRoleUpdate = useCallback(
    (data: { role?: string | undefined }) => {
      if (data?.role && data.role.length > 0) {
        setRole(data.role);
      }
    },
    [],
  );

  const handleNewPeerJoined = useCallback((data: NewPeerJoined) => {
    if (data.peer.peerId === props.peerId) {
      setRemotePeer(data.peer);
    }
  }, []);

  const handlePeerLeft = useCallback((data: RoomEvents['peer-left'][0]) => {
    if (peerId === data.peerId) {
      setRemotePeer(null);
    }
  }, []);

  const handleUpdateMetadata = useCallback(() => {
    const metadata = remotePeer?.getMetadata<T>();

    if (props?.onMetadataUpdate && metadata) {
      props.onMetadataUpdate({ metadata });
    }

    if (metadata) setMetadata(metadata);
    else setMetadata(null);
  }, [props?.onMetadataUpdate]);

  useEffect(() => {
    room.on('new-peer-joined', handleNewPeerJoined);

    room.on('peer-left', handlePeerLeft);

    if (remotePeer) {
      remotePeer?.on('metadata-updated', handleUpdateMetadata);

      remotePeer?.on('role-updated', handleRoleUpdate);
    }

    return () => {
      room.off('new-peer-joined', handleNewPeerJoined);

      room.off('peer-left', handlePeerLeft);

      remotePeer?.off('metadata-updated', handleUpdateMetadata);

      remotePeer?.off('role-updated', handleRoleUpdate);
    };
  }, [remotePeer, handleUpdateMetadata, handleRoleUpdate]);

  const peerId = remotePeer?.peerId;

  const updateRole = (
    role: string,
    options?: {
      custom: TPermissions;
    },
  ) => {
    if (remotePeer?.updateRole) {
      remotePeer.updateRole({ role, options });
    } else {
      console.warn('❌ RemotePeer does not Exist, cannot update role');
    }
  };

  return {
    peerId,
    role,
    metadata,
    updateRole,
  };
};
